import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import api from "../utils/api";
import { useAuthStore } from "./auth";

export interface MenuDefinition {
    key: string;
    label: string;
    group: string;
    icon: string;
}

export const usePermissionStore = defineStore("permissions", () => {
    const allowedMenus = ref<string[]>([]);
    const allMenus = ref<MenuDefinition[]>([]);
    const loaded = ref(false);
    const loading = ref(false);

    const authStore = useAuthStore();

    const isAdmin = computed(() => authStore.role === "admin");

    // Check if a menu key is allowed
    function canAccess(menuKey: string): boolean {
        // Admin always has full access
        if (isAdmin.value) return true;
        return allowedMenus.value.includes(menuKey);
    }

    // Fetch current user's permissions
    async function fetchMyPermissions() {
        if (isAdmin.value) {
            loaded.value = true;
            return;
        }
        loading.value = true;
        try {
            const res = await api.get("/extra/my-permissions");
            allowedMenus.value = res.data.menus || [];
            loaded.value = true;
        } catch {
            // If API fails, default to dashboard only
            allowedMenus.value = ["dashboard"];
            loaded.value = true;
        } finally {
            loading.value = false;
        }
    }

    // For admin: fetch all role permissions
    async function fetchAllRolePermissions() {
        const res = await api.get("/extra/role-permissions");
        allMenus.value = res.data.all_menus || [];
        return res.data;
    }

    // For admin: fetch permissions for a specific role
    async function fetchRolePermissions(role: string) {
        const res = await api.get(`/extra/role-permission?role=${role}`);
        return res.data;
    }

    // For admin: save permissions for a role
    async function saveRolePermissions(role: string, menus: string[]) {
        const res = await api.post("/extra/role-permission", { role, menus });
        return res.data;
    }

    // Reset on logout
    function reset() {
        allowedMenus.value = [];
        loaded.value = false;
    }

    // Auto-reset when user logs out (token becomes null)
    watch(() => authStore.isAuthenticated, (isAuth) => {
        if (!isAuth) {
            reset();
        }
    });

    return {
        allowedMenus,
        allMenus,
        loaded,
        loading,
        isAdmin,
        canAccess,
        fetchMyPermissions,
        fetchAllRolePermissions,
        fetchRolePermissions,
        saveRolePermissions,
        reset,
    };
});
